import { useMutation, UseMutationResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode } from "react";

import { DescriptionGroupContext, DescriptionGroupContextType } from "./DescriptionGroupContext";
import { descriptionGroupQueryKeys } from "./queryKeys";

import { instance } from "@/lib/axios/instance";
import { UpdateClientDescriptionsOrderRequest } from "@/types/request/UpdateClientDescriptionsOrderRequest";
import { UpdateDescriptionGroupsRequest } from "@/types/request/UpdateDescriptionGroupsRequest";
import { ErrorResponse } from "@/types/response/ErrorResponse";
import { GetDescriptionGroupsResponse } from "@/types/response/GetDescriptionGroupsResponse";
import { GetDescriptionsResponse } from "@/types/response/GetDescriptionsResponse";

type DescriptionGroupProviderProps = {
    children: ReactNode | ((context: DescriptionGroupContextType) => ReactNode);
    clientId: string;
    name: string;
    tenantId?: string | null;
};

export const DescriptionGroupProvider = ({ children, clientId, tenantId, name }: DescriptionGroupProviderProps) => {
  const queryClient = useQueryClient();

  const { data: descriptionGroups, isLoading: descriptionGroupsLoading, error: descriptionGroupsError } = useQuery<GetDescriptionGroupsResponse[], ErrorResponse>({
    queryKey: descriptionGroupQueryKeys.list(clientId, tenantId),
    queryFn: () => instance.get(`/api/v2/Descriptions/Groups`, { params: { clientId } }).then((res) => res.data.data),
    enabled: !!clientId
  });

  const { data: descriptions, isLoading: descriptionsLoading, error: descriptionsError } = useQuery<GetDescriptionsResponse[], ErrorResponse>({
    queryKey: descriptionGroupQueryKeys.descriptions(clientId, name, tenantId),
    queryFn: () => instance.get(`/api/v2/Descriptions/Groups/${name}`, { params: { clientId } }).then((res) => res.data.data),
    enabled: !!clientId && !!name
  });

  const saveDescriptionGroups: UseMutationResult<AxiosResponse<UpdateDescriptionGroupsRequest>, AxiosError<ErrorResponse>, UpdateDescriptionGroupsRequest> = useMutation(
    {
      mutationFn: (request: UpdateDescriptionGroupsRequest) => instance.put(`/api/v2/Descriptions/Groups/Save`, { ...request, clientId }),
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: descriptionGroupQueryKeys.list(clientId, tenantId) });
      },
    }
  );

  const updateClientDescriptions: UseMutationResult<AxiosResponse<UpdateClientDescriptionsOrderRequest>, AxiosError<ErrorResponse>, UpdateClientDescriptionsOrderRequest> = useMutation(
    {
      mutationFn: (request: UpdateClientDescriptionsOrderRequest) => instance.put(`/api/v2/Descriptions/Groups/${request.groupName}/Order`, { ...request, clientId }),
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries({ queryKey: descriptionGroupQueryKeys.descriptions(clientId, variables.groupName, tenantId) });
        queryClient.invalidateQueries({ queryKey: descriptionGroupQueryKeys.list(clientId, tenantId) });
      },
    }
  );

  const value = {
    descriptionGroups,
    descriptionGroupsError,
    descriptionGroupsLoading,
    descriptions,
    descriptionsError,
    descriptionsLoading,
    updateClientDescriptionsOrder: updateClientDescriptions,
    updateClientDescriptionsOrderError: updateClientDescriptions.failureReason?.response?.data,
    updateClientDescriptionsOrderPending: updateClientDescriptions.isPending,
    saveDescriptionGroups,
    saveDescriptionGroupsError: saveDescriptionGroups.failureReason?.response?.data,
    saveDescriptionGroupsPending: saveDescriptionGroups.isPending,
  };

  return (
    <DescriptionGroupContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </DescriptionGroupContext.Provider>
  );
};
